import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Send, Check, XCircle, MessageSquare, ShoppingBag } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";

/**
 * NegotiationModal — chat + offer flow between buyer / seller / admin for a thrifted product.
 * Props:
 *   productId — required if `negotiationId` not provided (buyer opens chat from product page)
 *   negotiationId — required if you have an existing negotiation
 *   onClose — close callback
 *   onAccepted?({accepted_price}) — fired when an offer is accepted
 */
export default function NegotiationModal({ productId, negotiationId, onClose, onAccepted }) {
  const { user } = useAuth();
  const { add } = useCart();
  const navigate = useNavigate();
  const [neg, setNeg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [offer, setOffer] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (negotiationId) {
        const { data } = await api.get(`/negotiations/${negotiationId}`);
        setNeg(data);
      } else if (productId) {
        const { data } = await api.get(`/negotiations/lookup/by-product/${productId}`);
        if (data) setNeg(data);
        else {
          // start empty (create on first send)
          setNeg({ _local: true, product_id: productId });
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load chat");
      onClose?.();
    } finally {
      setLoading(false);
    }
  }, [negotiationId, productId, onClose]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [neg]);

  const myRole = !user ? "guest" : user.role === "admin" ? "admin" : neg?.seller_id === user.user_id ? "seller" : "buyer";

  const buyAtBargain = async () => {
    if (!neg?.product_id || !neg?.accepted_price) return;
    try {
      const { data: p } = await api.get(`/products/${neg.product_id}`);
      if (!p || (p.stock ?? 0) <= 0) return toast.error("This item is no longer available");
      const size = (p.sizes || [])[0] || null;
      const color = (p.colors || [])[0] || null;
      // Pass a synthetic product object with the negotiated price so cart shows the deal price
      add({ ...p, price: neg.accepted_price }, { size, color });
      toast.success(`Locked at ₹${neg.accepted_price} — heading to checkout`);
      onClose?.();
      navigate("/checkout");
    } catch (err) {
      toast.error("Couldn't add item — try again");
    }
  };

  const send = async () => {
    if (!text.trim() && !offer) return toast.error("Type a message or an offer");
    const offerNum = offer ? Number(offer) : null;
    if (offerNum !== null && offerNum <= 0) return toast.error("Offer must be > 0");
    setSending(true);
    try {
      if (!neg || neg._local) {
        const { data } = await api.post("/negotiations/start", {
          product_id: productId,
          text: text || null,
          offer_price: offerNum,
        });
        setNeg(data);
      } else {
        await api.post(`/negotiations/${neg.negotiation_id}/message`, {
          text: text || null,
          offer_price: offerNum,
        });
        await load();
      }
      setText("");
      setOffer("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const respond = async (action) => {
    try {
      const { data } = await api.post(`/negotiations/${neg.negotiation_id}/message`, {
        text: null, offer_price: null, action,
      });
      if (action === "accept") {
        toast.success(`Deal locked at ₹${data.accepted_price}`);
        onAccepted?.({ accepted_price: data.accepted_price });
      } else {
        toast.success("Offer rejected");
      }
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    }
  };

  const latestOtherOffer = () => {
    if (!neg?.messages) return null;
    return [...neg.messages].reverse().find(
      (m) => m.offer_price && (myRole === "admin" ? true : m.sender_role !== myRole)
    );
  };
  const otherOffer = latestOtherOffer();

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="negotiation-modal">
      <div className="bg-white border-2 border-black brutal-shadow-lg w-full max-w-lg h-[85vh] sm:h-[70vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-black text-[#D4FF00] border-b-2 border-black p-3 flex items-center justify-between">
          <div>
            <p className="font-mono-b text-[10px] uppercase tracking-widest opacity-70">BARGAIN CHAT · {myRole.toUpperCase()}</p>
            <p className="font-display text-xl leading-none">{neg?.product_name || "Negotiation"}</p>
            {neg?.list_price && <p className="font-mono-b text-[11px] opacity-80">List: ₹{neg.list_price}{neg.accepted_price && ` · Accepted: ₹${neg.accepted_price}`}</p>}
          </div>
          <button onClick={onClose} data-testid="nego-close" className="border-2 border-[#D4FF00] p-1"><X className="w-4 h-4" /></button>
        </div>

        {/* Status pill */}
        {neg?.status && (
          <div className={`px-3 py-2 font-mono-b text-[11px] border-b-2 border-black ${neg.status === "accepted" ? "bg-[#D4FF00]" : neg.status === "rejected" || neg.status === "expired" ? "bg-[#FF007F] text-white" : "bg-[#F4F4F0]"}`}>
            <b>STATUS:</b> {neg.status.toUpperCase()}{neg.accepted_price ? ` · ₹${neg.accepted_price}` : ""}
          </div>
        )}

        {/* Auto-Buy CTA — only visible to the buyer once accepted */}
        {neg?.status === "accepted" && neg?.accepted_price && myRole === "buyer" && (
          <div className="border-b-2 border-black bg-black text-[#D4FF00] p-3" data-testid="nego-buy-strip">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <p className="font-mono-b text-[10px] uppercase tracking-widest opacity-70">DEAL LOCKED</p>
                <p className="font-display text-2xl leading-none mt-1">BUY AT ₹{neg.accepted_price}</p>
                <p className="font-mono-b text-[10px] opacity-70 mt-1">Saved ₹{(neg.list_price - neg.accepted_price).toFixed(2)} off list</p>
              </div>
              <button
                onClick={buyAtBargain}
                data-testid="nego-buy-now"
                className="bg-[#D4FF00] text-black border-2 border-[#D4FF00] px-4 py-3 font-mono-b font-bold uppercase inline-flex items-center gap-2 hover-nudge"
              >
                <ShoppingBag className="w-4 h-4" /> BUY NOW
              </button>
            </div>
          </div>
        )}

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2 font-mono-b text-sm bg-[#F4F4F0]" data-testid="nego-messages">
          {loading ? (
            <p className="text-black/50 text-center p-6">Loading...</p>
          ) : !neg?.messages || neg.messages.length === 0 ? (
            <div className="text-center p-6">
              <MessageSquare className="w-8 h-8 mx-auto opacity-40" />
              <p className="text-black/60 text-xs mt-2">No messages yet. Make an offer to get started.</p>
            </div>
          ) : (
            neg.messages.map((m) => {
              const mine = m.sender_id === user?.user_id;
              return (
                <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.message_id}`}>
                  <div className={`max-w-[80%] border-2 border-black p-2 ${mine ? "bg-[#D4FF00]" : m.sender_role === "admin" ? "bg-black text-[#D4FF00]" : "bg-white"} ${m.action === "accept" ? "ring-4 ring-green-500 ring-inset" : m.action === "reject" ? "ring-4 ring-[#FF007F] ring-inset" : ""}`}>
                    <p className="text-[9px] uppercase tracking-widest opacity-60">{m.sender_role} · {m.sender_name}</p>
                    {m.offer_price != null && (
                      <p className="font-display text-lg leading-none mt-1">
                        {m.action === "accept" ? "ACCEPTED · " : "OFFER · "}₹{m.offer_price}
                      </p>
                    )}
                    {m.text && <p className="mt-1 text-xs">{m.text}</p>}
                    {m.action === "reject" && <p className="text-[10px] font-bold mt-1">DECLINED</p>}
                    <p className="text-[9px] opacity-50 mt-1">{new Date(m.created_at).toLocaleString()}</p>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Actions bar (seller/admin can accept/reject the latest counter-offer) */}
        {neg && neg.status === "open" && otherOffer && (myRole === "seller" || myRole === "admin") && (
          <div className="border-t-2 border-black bg-[#F4F4F0] px-3 py-2 flex flex-wrap items-center gap-2" data-testid="nego-decision-bar">
            <span className="font-mono-b text-xs">Latest offer: <b>₹{otherOffer.offer_price}</b></span>
            <button onClick={() => respond("accept")} data-testid="nego-accept" className="ml-auto bg-[#D4FF00] border-2 border-black px-3 py-1 font-mono-b text-xs font-bold uppercase inline-flex items-center gap-1 hover-nudge"><Check className="w-3 h-3" /> ACCEPT</button>
            <button onClick={() => respond("reject")} data-testid="nego-reject" className="bg-[#FF007F] text-white border-2 border-black px-3 py-1 font-mono-b text-xs font-bold uppercase inline-flex items-center gap-1 hover-nudge"><XCircle className="w-3 h-3" /> REJECT</button>
          </div>
        )}

        {/* Input */}
        {neg && neg.status !== "rejected" && neg.status !== "expired" && (
          <div className="border-t-2 border-black p-3 space-y-2">
            <div className="flex gap-2">
              <input
                type="number"
                min="1"
                placeholder="Offer ₹"
                data-testid="nego-offer-input"
                value={offer}
                onChange={(e) => setOffer(e.target.value)}
                className="brutal-input w-28 text-sm"
              />
              <input
                placeholder="Say something..."
                data-testid="nego-text-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !sending && send()}
                className="brutal-input flex-1 text-sm"
              />
              <button
                onClick={send}
                disabled={sending}
                data-testid="nego-send"
                className="bg-black text-[#D4FF00] border-2 border-black px-3 font-mono-b font-bold uppercase text-xs hover-nudge disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[10px] font-mono-b text-black/50">Offers must be less than ₹{neg.list_price || "-"}. Only the seller or an admin can accept.</p>
          </div>
        )}
      </div>
    </div>
  );
}
